import React, { FC, useMemo } from 'react';
import { Progress } from 'antd';
import { useTheme } from 'styled-components';
import { useTranslate } from '@/translations';
import { useStockByCompound } from '@/api/OperationsDashboard';
import { ChartCard } from './ChartCard';

const HEIGHT = 300;

export const StockByCompoundChart: FC = () => {
  const { t } = useTranslate();
  const { accent, primaryChartColor, textColor } = useTheme();
  const { data, isLoading, isError, refetch } = useStockByCompound();

  const { rows, useCapacity, maxCount } = useMemo(() => {
    const all = data ?? [];
    const withCapacity = all.filter((r) => r.capacity > 0);
    const capacityMode = withCapacity.length > 0;
    const sorted = capacityMode
      ? [...withCapacity].sort(
          (a, b) => (b.utilisationPct ?? 0) - (a.utilisationPct ?? 0),
        )
      : [...all].sort((a, b) => b.count - a.count);
    return {
      rows: sorted,
      useCapacity: capacityMode,
      maxCount: Math.max(1, ...all.map((r) => r.count)),
    };
  }, [data]);

  const colorFor = (pct: number) =>
    pct >= 90 ? '#e53935' : pct >= 75 ? accent : primaryChartColor;

  return (
    <ChartCard
      title={t('dashboard.widget.stockByCompound')}
      isLoading={isLoading}
      isError={isError}
      isEmpty={data?.length === 0}
      onRetry={() => refetch()}
      height={HEIGHT}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          maxHeight: HEIGHT,
          overflowY: 'auto',
        }}
      >
        {rows.map((r) => {
          const percent = useCapacity
            ? (r.utilisationPct ?? 0)
            : Math.round((r.count / maxCount) * 100);
          return (
            <div key={r.compound}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                  marginBottom: 2,
                }}
              >
                <span style={{ color: textColor }}>{r.compound}</span>
                <span style={{ color: textColor, opacity: 0.6 }}>
                  {useCapacity
                    ? `${r.utilisationPct}% (${r.count.toLocaleString()} / ${r.capacity.toLocaleString()})`
                    : r.count.toLocaleString()}
                </span>
              </div>
              <Progress
                percent={percent}
                showInfo={false}
                strokeColor={colorFor(percent)}
              />
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
};
